import React, { useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import { rarityColors, statusColors } from '../../theme/tokens/base';
import GradualBlur from './GradualBlur';

const STACK_CONFIG = [
  { scale: 1, y: 0, opacity: 1 },
  { scale: 0.9, y: 28, opacity: 1 },
  { scale: 0.8, y: 44, opacity: 1 },
];

const DISMISS_THRESHOLD = 150;

function AchievementCard({ achievement, index, isDark, onDismiss, accentColor }) {
  const isTop = index === 0;
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-8, 8]);
  const exitDirectionRef = useRef({ x: 0, y: -200 });
  const config = STACK_CONFIG[index] || STACK_CONFIG[2];
  const rarityColor = rarityColors[achievement.rarity] || accentColor || '#4CAF50';
  const hasDragged = useRef(false);

  const handleDragStart = () => {
    hasDragged.current = false;
  };

  const handleDrag = (_, info) => {
    if (Math.abs(info.offset.x) > 10 || Math.abs(info.offset.y) > 10) {
      hasDragged.current = true;
    }
  };

  const handleDragEnd = (_, info) => {
    const absX = Math.abs(info.offset.x);
    const absVelocityX = Math.abs(info.velocity.x);
    const shouldDismiss = absX > DISMISS_THRESHOLD || absVelocityX > 500 || info.offset.y < -80;
    if (shouldDismiss) {
      exitDirectionRef.current = {
        x: info.offset.x > 0 ? 300 : -300,
        y: info.offset.y < -80 ? -200 : 0,
      };
      onDismiss();
    }
  };

  const handleTap = () => {
    if (!hasDragged.current) {
      exitDirectionRef.current = { x: 200, y: -100 };
      onDismiss();
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 80, scale: 0.8 }}
      animate={{
        opacity: config.opacity,
        y: config.y,
        scale: config.scale,
        transition: { type: 'spring', stiffness: 300, damping: 25, delay: index * 0.12 },
      }}
      exit={{
        x: exitDirectionRef.current.x,
        y: exitDirectionRef.current.y,
        opacity: 0,
        scale: 0.5,
        transition: { duration: 0.3 },
      }}
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 3 - index,
        x: isTop ? x : 0,
        rotate: isTop ? rotate : 0,
        touchAction: 'none',
      }}
      drag={isTop}
      dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
      dragElastic={0.7}
      dragSnapToOrigin
      onDragStart={isTop ? handleDragStart : undefined}
      onDrag={isTop ? handleDrag : undefined}
      onDragEnd={isTop ? handleDragEnd : undefined}
      onTap={isTop ? handleTap : undefined}
      whileTap={isTop ? { scale: 0.97 } : undefined}
    >
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '50px',
          border: `2px solid ${rarityColor}`,
          bgcolor: isDark ? 'background.default' : '#fff',
          boxShadow: `0 4px 20px ${rarityColor}44`,
          px: 2.5,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          cursor: isTop ? 'grab' : 'default',
          userSelect: 'none',
        }}
      >
        <Typography sx={{ fontSize: '2rem', lineHeight: 1 }}>
          {achievement.icon || '🏅'}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight={900}
            sx={{ color: rarityColor, lineHeight: 1.2 }}
          >
            {achievement.nimi}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
              lineHeight: 1.2,
              display: 'block',
            }}
          >
            {achievement.kuvaus}
          </Typography>
        </Box>
        {achievement.coin_reward > 0 && (
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{ color: statusColors.goal, whiteSpace: 'nowrap' }}
          >
            +{achievement.coin_reward} 🪙
          </Typography>
        )}
        {/* Depth blur for cards behind the top */}
        {index > 0 && (
          <GradualBlur
            position="top"
            strength={index * 0.0}
            height="100%"
            divCount={3}
            target="parent"
            zIndex={10}
          />
        )}
      </Box>
    </motion.div>
  );
}

export default function AchievementStack({
  achievements = [],
  onDismiss,
  onAllDismissed,
  isDark = false,
  visible = true,
  accentColor,
}) {
  const visibleCards = achievements.slice(0, 3);

  useEffect(() => {
    if (achievements.length === 0 && visible) {
      onAllDismissed?.();
    }
  }, [achievements.length, visible, onAllDismissed]);

  if (!visible || visibleCards.length === 0) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: { xs: 140, md: 140 },
        left: '10%',
        right: '10%',
        zIndex: 20,
        height: 90,
      }}
    >
      <AnimatePresence mode="popLayout">
        {visibleCards.map((achievement, index) => (
          <AchievementCard
            key={achievement.type || achievement.achievement_type || index}
            achievement={achievement}
            index={index}
            isDark={isDark}
            onDismiss={onDismiss}
            accentColor={accentColor}
          />
        ))}
      </AnimatePresence>
    </Box>
  );
}
